import { classNames, h, signal, tag } from "omi";

import { UploadValidation } from "@/components/validations/models";
import { tailwind } from "@/tailwind";
import { decodeData } from "@/utils";

import { DocBase } from "../docBase";
import { DocumentTheme, DocumentThemeDef } from "../models";

type Props = {
  imgSrc: string;
  caption?: string;
  size: "xs" | "sm" | "md" | "lg" | "xl";
  aspectRatio?: "2/3" | "4/3" | "3/1" | "16/9";
  padding?: "xs" | "sm" | "md" | "lg" | "xl";
  cover?: boolean;
  theme: DocumentTheme;
};

@tag("nw-doc-image")
export class NWDocImage extends DocBase<Props> {
  static css = [tailwind, ":host { box-sizing: border-box; }"];

  static propTypes = {
    caption: String,
    imgSrc: String,
    aspectRatio: String,
    padding: String,
    size: String,
    cover: Boolean,
    theme: String,
  };

  private src = signal<string | undefined>(undefined);

  installed() {
    const hydrationData = this.getAttribute("data");
    if (hydrationData == null) {
      return;
    }

    const validation = decodeData<UploadValidation>(hydrationData);
    this.src.value = this.props.imgSrc || `../../uploads/${validation.file?.storageName}`;
  }

  getElementHeight() {
    const container = this.shadowRoot?.querySelector("#container");
    return container?.getBoundingClientRect().height ?? 0;
  }

  render({ aspectRatio = "2/3", padding = "lg", size = "sm", cover = false, theme }: Props) {
    const paddingClasses = this.getPaddingClasses(padding);
    const themeDef = DocumentThemeDef[theme];

    return (
      <div
        id="container"
        class={classNames(`w-full h-full mt-4 grow rounded-xl flex items-center`, paddingClasses, themeDef.frame)}
      >
        {this.src.value && (
          <div
            style={{ "--image-url": `url(${this.src.value})` }}
            class={classNames(
              `aspect-[${aspectRatio}] w-full bg-contain bg-center bg-no-repeat rounded-xl flex-1`,
              {
                "bg-[image:var(--image-url)]": this.src.value,
              },
              {
                "bg-cover": cover,
                "bg-contain": !cover,
              },
              { "h-[100px]": size === "xs" },
              { "h-[200px]": size === "sm" },
              { "h-[300px]": size === "md" },
              { "h-[400px]": size === "lg" },
              { "h-[500px]": size === "xl" },
            )}
          ></div>
        )}
      </div>
    );
  }

  private getPaddingClasses(padding: string): string {
    switch (padding) {
      case "xs":
        return "px-4 py-4";

      case "sm":
        return "p-14";

      default:
      case "md":
      case "lg":
        return "py-16 px-16";
    }
  }
}
